import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Clock, Camera, Zap, SlidersHorizontal, Briefcase } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAnnonces, getUrgencyLabel } from "@/hooks/useAnnonces";
import { ANNONCE_CATEGORIES } from "@/lib/constants";
import { getCategoryColors } from "@/lib/categoryColors";
import EmptyState from "@/components/EmptyState";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import { cn } from "@/lib/utils";
import { useIsArtisan, useArtisanProfile } from "@/hooks/useArtisanProfile";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import { useWilayas } from "@/hooks/useAlgeriaData";

const getUrgencyClass = (urgency?: string) => {
  if (urgency === "asap") return "urgency-asap";
  if (urgency === "thisWeek") return "urgency-week";
  return "urgency-flex";
};

const Annonces = () => {
  const { t, locale } = useLanguage("annonces");
  const lp = useLocalizedPath();
  const allWilayas = useWilayas(locale);

  useSEO({
    title: t("list.title"),
    description: t("list.subtitle"),
    canonical: "/annonces",
    locale,
  });

  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") ?? "tous";
  const [filterWilaya, setFilterWilaya] = useState("toutes");
  const [filterCategory, setFilterCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const { data: isArtisan } = useIsArtisan();
  const { data: profile } = useArtisanProfile();

  useEffect(() => {
    if (isArtisan && profile && !filtersInitialized) {
      if (profile.wilaya) setFilterWilaya(profile.wilaya);
      if (profile.metier && initialCategory === "tous") setFilterCategory(profile.metier);
      setFiltersInitialized(true);
    }
  }, [isArtisan, profile, filtersInitialized, initialCategory]);

  const { data: annonces = [], isLoading } = useAnnonces({
    wilaya: filterWilaya !== "toutes" ? filterWilaya : undefined,
    category: filterCategory !== "tous" ? filterCategory : undefined,
  });

  const filtered = annonces.filter((a) =>
    !searchQuery || a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasActiveFilters = filterWilaya !== "toutes" || filterCategory !== "tous" || searchQuery;

  const categories = [
    { value: "tous", label: t("list.all_categories") },
    ...ANNONCE_CATEGORIES.map((c) => ({
      value: c.value,
      label: locale === "ar" ? c.label_ar : c.label,
    })),
  ];

  const formatBudget = (min?: number | null, max?: number | null) => {
    const currency = locale === "ar" ? "دج" : "DA";
    if (min && max) return `${min.toLocaleString()} – ${max.toLocaleString()} ${currency}`;
    if (min) return locale === "ar" ? `ابتداءً من ${min.toLocaleString()} ${currency}` : `À partir de ${min.toLocaleString()} ${currency}`;
    if (max) return locale === "ar" ? `حتى ${max!.toLocaleString()} ${currency}` : `Jusqu'à ${max!.toLocaleString()} ${currency}`;
    return null;
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <Navbar />

      {/* Sticky filter bar */}
      <div className="sticky top-16 z-40 border-b bg-card/95 backdrop-blur-md shadow-warm-sm">
        <div className="container py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder={t("filters.search_placeholder")}
                className="ps-9 h-10 bg-background"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={t("list.filter_label")}
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <Select value={filterWilaya} onValueChange={setFilterWilaya}>
                <SelectTrigger className="h-10 w-full sm:w-[160px] bg-background" aria-label={t("list.all_wilayas")}>
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground me-1.5 shrink-0" aria-hidden="true" />
                  <SelectValue placeholder={t("list.all_wilayas")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="toutes">{t("filters.all_wilayas")}</SelectItem>
                  {allWilayas
                    .sort((a, b) => a.label.localeCompare(b.label, locale === "ar" ? "ar" : "fr"))
                    .map((w) => (
                      <SelectItem key={w.state_id} value={w.state_name}>{w.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-10 w-full sm:w-[180px] bg-background" aria-label={t("list.all_categories")}>
                  <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground me-1.5 shrink-0" aria-hidden="true" />
                  <SelectValue placeholder={t("list.all_categories")} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <main className="container py-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">{t("list.title")}</h1>
            {!isLoading && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {filtered.length <= 1
                  ? t("count.one", { count: filtered.length })
                  : t("count.other", { count: filtered.length })}
                {hasActiveFilters && t("count.filtered")}
              </p>
            )}
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground text-xs"
              onClick={() => { setFilterWilaya("toutes"); setFilterCategory("tous"); setSearchQuery(""); }}
            >
              {t("filters.reset")}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-44 rounded-xl border bg-card animate-pulse shadow-warm-sm" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="shadow-warm-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={Briefcase}
                title={
                  filterWilaya !== "toutes" && filterCategory !== "tous"
                    ? t("empty.no_demand_category_wilaya", {
                        category: categories.find(c => c.value === filterCategory)?.label ?? filterCategory,
                        wilaya: filterWilaya,
                      })
                    : filterWilaya !== "toutes"
                    ? t("empty.no_demand_wilaya", { wilaya: filterWilaya })
                    : filterCategory !== "tous"
                    ? t("empty.no_demand_category", {
                        category: categories.find(c => c.value === filterCategory)?.label ?? filterCategory,
                      })
                    : t("empty.no_demand_default")
                }
                description={
                  isArtisan
                    ? t("empty.desc_artisan")
                    : t("empty.desc_client")
                }
                primaryAction={
                  isArtisan
                    ? { label: t("empty.cta_profile"), href: lp("/mon-profil-artisan") }
                    : { label: t("empty.cta_poster"), href: lp("/poster-annonce") }
                }
                secondaryAction={
                  hasActiveFilters
                    ? {
                        label: t("empty.cta_all"),
                        onClick: () => { setFilterWilaya("toutes"); setFilterCategory("tous"); setSearchQuery(""); },
                      }
                    : undefined
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filtered.map((annonce) => {
              const catKey = annonce.category || annonce.metier;
              const catColors = getCategoryColors(catKey);
              return (
              <article
                key={annonce.id}
                className={cn(
                  "group relative rounded-xl border border-s-4 bg-card shadow-warm-sm overflow-hidden",
                  "transition-all duration-200 hover:shadow-warm-md hover:border-primary/20 hover:-translate-y-0.5",
                  catColors.border,
                  getUrgencyClass(annonce.urgency)
                )}
              >
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <Badge
                      variant="outline"
                      className={cn("text-xs gap-1 border", catColors.badgeBg, catColors.badgeText)}
                    >
                      {t(`categories_all.${catKey}`, { defaultValue: catKey })}
                    </Badge>
                    {annonce.urgency === "asap" && (
                      <Badge className="bg-destructive text-destructive-foreground text-xs gap-1">
                        <Zap className="h-3 w-3" aria-hidden="true" />
                        {t("badge.urgent")}
                      </Badge>
                    )}
                    {annonce.urgency === "thisWeek" && (
                      <Badge variant="warning" className="text-xs">{t("badge.this_week")}</Badge>
                    )}
                  </div>

                  <h2 className="font-display text-base font-semibold leading-snug mb-1.5 group-hover:text-primary transition-colors">
                    {annonce.title}
                  </h2>
                  {annonce.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {annonce.description}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {annonce.commune}, {annonce.wilaya}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {getUrgencyLabel(annonce.urgency, locale)}
                    </span>
                    {formatBudget(annonce.budget_min, annonce.budget_max) && (
                      <span className="flex items-center gap-1 font-semibold text-foreground">
                        {formatBudget(annonce.budget_min, annonce.budget_max)}
                      </span>
                    )}
                    {annonce.photos.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Camera className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {annonce.photos.length > 1
                          ? t("photos_plural", { count: annonce.photos.length })
                          : t("photos", { count: annonce.photos.length })}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-3">
                    <span className="text-xs text-muted-foreground">
                      {new Date(annonce.created_at).toLocaleDateString(locale === "ar" ? "ar-DZ" : "fr-DZ", { day: "numeric", month: "short" })}
                    </span>
                    <Link to={lp(`/annonce/${annonce.id}`)}>
                      <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs font-medium group-hover:text-primary transition-colors">
                        {t("see_details")}
                        <DirectionalArrow className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            );
            })}
          </div>
        )}
      </main>

      <div className="hidden md:block"><Footer /></div>
      <ArtisanBottomNav />
    </div>
  );
};

export default Annonces;
